/**
 * Emergency Equipment & Procedures – Checklist Items
 * HYBRID APPROACH: WBC3 Compliance + Industry Best Practice
 *
 * CORE FOUNDATION - WBC3 Appendix 8, Section 7.2 General Familiarisation:
 * "Prior to the first occasion of working on the vessel, each worker must receive appropriate familiarisation training"
 * Cross-referenced with WBC3 Section 14.11.3 Life-saving appliances familiarisation
 *
 * STRUCTURE:
 * - WBC3 Required: Cannot be marked N/A, verified section references
 * - Best Practice: Vessel-specific extensions, can be marked N/A if not applicable
 *
 * TAGS:
 * - "Required": Always applies (WBC3 base requirements)
 * - "WBC3 Required": Vessel-specific WBC3 requirements
 * - "Best Practice": Industry best practice, vessel-specific
 * - "Conditional": Equipment or operations based
 */

import { parseCategory } from '../../utils/categoryHelpers';

// Helper for vessel type normalization
const normalizeVesselType = (type) => {
  return (type || '').toLowerCase().trim();
};

export const EMERGENCY_EQUIPMENT_PROCEDURES_ITEMS = [
  // BASE ITEMS - Always Required (WBC3 Section 22.4 Familiarisation Requirements)
  {
    id: 'fire_extinguisher_operation_maintenance',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Fire Extinguisher Operation & Maintenance',
    trigger: 'Always',
    reference: 'General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Ensure designated crew understand correct use, inspection routines, and servicing responsibility.'
  },
  {
    id: 'lifejacket_location_donning',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Location and donning of lifejackets',
    trigger: 'Always',
    reference: 'Life-saving appliances familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Show all lifejacket storage locations and demonstrate proper donning technique.'
  },
  {
    id: 'emergency_drills_mob_recovery',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Emergency drills and MOB recovery procedures',
    trigger: 'Always',
    reference: 'General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Explain emergency drill procedures and demonstrate MOB recovery equipment operation.'
  },
  {
    id: 'first_aid_kit_location',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Location of first aid kit(s)',
    trigger: 'Always',
    reference: 'General Familiarisation (cross-ref Section 23.1.1)',
    tag: 'Required',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Show first aid kit locations and explain basic contents overview.'
  },
  {
    id: 'emergency_communication_location',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Location of emergency communication equipment',
    trigger: 'Always',
    reference: 'General Familiarisation (cross-ref Section 17.2.1)',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Show location of VHF radio, DSC controller, and emergency communication equipment.'
  },
  {
    id: 'emergency_communication_operation',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Operation of emergency communication equipment',
    trigger: 'Always',
    reference: 'General Familiarisation (cross-ref Section 17.1)',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Demonstrate VHF operation, DSC distress calling, and emergency communication procedures.'
  },
  {
    id: 'vessel_evacuation_routes',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Evacuation routes from all vessel areas',
    trigger: 'Always',
    reference: 'General Familiarisation',
    tag: 'Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Walk through all evacuation routes and ensure crew understands escape paths from every area.'
  },

  // CONDITIONAL ITEMS - Equipment Based
  {
    id: 'life_raft_location_deployment',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Life raft location and deployment procedures',
    trigger: (vessel) => vessel.liferafts_fitted === true,
    reference: 'Life-saving appliances familiarisation',
    tag: 'Conditional',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    naReasons: ['No life rafts fitted', 'Not required for vessel category'],
    tooltip: 'Life raft deployment procedures and emergency use. CUSTOMIZE: Add your vessel\'s raft locations and launching procedures. (WBC3 Section 28.1.6)'
  },
  {
    id: 'epirb_location_activation',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'EPIRB location and activation procedures',
    trigger: (vessel) => vessel.epirb_required === true,
    reference: 'Life-saving appliances familiarisation',
    tag: 'Conditional',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    naReasons: ['EPIRB not required', 'EPIRB not fitted'],
    tooltip: 'EPIRB location and activation procedures. CUSTOMIZE: Add your EPIRB model, HEX ID, and activation procedures. (WBC3 Section 28.1.6)'
  },
  {
    id: 'emergency_lighting_systems',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Emergency lighting and backup systems',
    trigger: (vessel) => vessel.enclosed_accommodation_present === true,
    reference: 'General Familiarisation (cross-ref Section 9.7.1)',
    tag: 'Conditional',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    naReasons: ['No enclosed accommodation', 'Emergency lighting not fitted'],
    tooltip: 'Emergency lighting operation and backup power. CUSTOMIZE: Document locations and test procedures. (WBC3 Section 28.1.6)'
  },
  {
    id: 'pyrotechnic_signals',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Pyrotechnic signals location and use',
    trigger: (vessel) => {
      // parseCategory handles 'cat3', 3, '3', 'CAT3', 'IWW' formats
      const cat = parseCategory(vessel.category);
      return cat <= 2;
    },
    reference: 'Life-saving appliances familiarisation',
    tag: 'Conditional',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    naReasons: ['No pyrotechnics carried', 'Not required for vessel category'],
    tooltip: 'Pyrotechnic signal storage and use. CUSTOMIZE: Add types carried, expiry dates, and storage locations. (WBC3 Section 28.1.6)'
  },

  // CONDITIONAL ITEMS - Vessel Type Based (WBC3 Required)
  {
    id: 'towing_emergency_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Emergency procedures for towing operations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Safety Equipment Standards',
    tag: 'WBC3 Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Emergency towing procedures and safety protocols. CUSTOMIZE: Add your vessel\'s emergency towing procedures. (WBC3 Section 28.1.6)'
  },
  {
    id: 'pilot_transfer_safety',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Pilot transfer safety procedures and equipment',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Lifting Operations (ref. MGN 50)',
    tag: 'WBC3 Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Pilot transfer safety equipment and procedures. CUSTOMIZE: Add your port\'s specific transfer protocols. (WBC3 Section 28.1.6)'
  },
  {
    id: 'enhanced_emergency_familiarisation',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Enhanced familiarisation with life-saving appliances',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'patrol',
    reference: 'Police Boats Enhanced Requirements',
    tag: 'WBC3 Required',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Enhanced patrol vessel LSA familiarisation. CUSTOMIZE: Add your force\'s enhanced safety requirements. (WBC3 Section 28.1.6)'
  },

  // VESSEL-SPECIFIC BEST PRACTICE ITEMS
  // PASSENGER Vessels - Industry Best Practice
  {
    id: 'passenger_pfd_assistance',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Instructions for assisting passengers with lifejackets',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax',
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['No passengers carried', 'Crew-only operations'],
    tooltip: 'Passenger lifejacket assistance training. CUSTOMIZE: Add your vessel\'s specific lifejacket types and assistance procedures. (WBC3 Section 28.1.6)'
  },
  {
    id: 'passenger_muster_stations',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Location of passenger muster stations and assembly points',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax',
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['No designated muster stations', 'Open deck vessel'],
    tooltip: 'Passenger muster station locations. CUSTOMIZE: Document muster station positions and signage. (WBC3 Section 28.1.6)'
  },
  {
    id: 'passenger_crowd_control',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Passenger crowd control procedures in emergencies',
    trigger: (vessel) => vessel.passengerCapacity > 12,
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['12 or fewer passengers', 'No passenger operations'],
    tooltip: 'Crowd control for large passenger groups. CUSTOMIZE: Add procedures for your vessel capacity. (WBC3 Section 28.1.6)'
  },

  // TUG Vessels - Industry Best Practice
  {
    id: 'emergency_fuel_shutoffs',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Emergency fuel shut-offs and engine stops',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    tooltip: 'Emergency fuel and engine stop locations. CUSTOMIZE: Add your vessel\'s emergency stop positions. (WBC3 Section 28.1.6)'
  },
  {
    id: 'towline_emergency_release',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Towline emergency release systems and procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    tooltip: 'Towline emergency release operation. CUSTOMIZE: Add your vessel\'s release system type and procedures. (WBC3 Section 28.1.6)'
  },

  // RIB/PILOT BOAT - Industry Best Practice
  {
    id: 'high_speed_mob_recovery',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'High-speed MOB recovery equipment and procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'rib' || normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    tooltip: 'High-speed MOB recovery for RIBs and pilot boats. CUSTOMIZE: Add your vessel\'s MOB equipment locations. (WBC3 Section 28.1.6)'
  },

  // CREW TRANSFER - Industry Best Practice
  {
    id: 'transfer_safety_equipment',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Personnel transfer safety equipment and procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv',
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['No personnel transfer operations', 'Cargo only'],
    tooltip: 'CTV personnel transfer safety equipment. CUSTOMIZE: Add your transfer equipment and procedures. (WBC3 Section 28.1.6)'
  },

  // OFFSHORE CATEGORIES - Industry Best Practice
  {
    id: 'offshore_survival_equipment',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Extended offshore survival equipment verification',
    trigger: (vessel) => {
      // parseCategory handles 'cat3', 3, '3', 'CAT3', 'IWW' formats
      const cat = parseCategory(vessel.category);
      return cat <= 2;
    },
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['Inshore operations only', 'Limited offshore equipment'],
    tooltip: 'Extended offshore survival equipment verification. CUSTOMIZE: Add your vessel\'s offshore survival equipment inventory. (WBC3 Section 28.1.6)'
  },

  // EQUIPMENT-BASED BEST PRACTICE
  {
    id: 'emergency_escape_routes',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Emergency escape route markings and signage',
    trigger: (vessel) => vessel.enclosed_accommodation_present === true,
    reference: 'Industry Best Practice',
    tag: 'Best Practice',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['No enclosed accommodation', 'Open deck vessel'],
    tooltip: 'Emergency escape route markings and signage. CUSTOMIZE: Document escape routes and signage locations. (WBC3 Section 28.1.6)'
  },

  // CONDITIONAL ITEMS - Operations Based
  {
    id: 'dangerous_goods_familiarisation',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'General awareness/familiarisation training in transport of dangerous goods',
    trigger: (vessel) => vessel.operations && vessel.operations.includes('dangerous_goods'),
    reference: 'Dangerous goods familiarisation',
    tag: 'Conditional',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    naReasons: ['No dangerous goods carried', 'Not applicable to vessel operations'],
    tooltip: 'Dangerous goods handling and emergency response. CUSTOMIZE: Add specific DG classes and emergency procedures. (WBC3 Section 28.1.6)'
  }
];

/**
 * Generate Emergency Equipment & Procedures checklist
 * WBC3-compliant familiarisation training per Appendix 8, Section 7.2 General Familiarisation
 * and Section 14.11.3 Life-saving appliances familiarisation
 * 
 * Implements vessel-specific conditional logic for emergency equipment
 * and procedures familiarisation training requirements
 */
export function generateEmergencyEquipmentChecklist(vessel, crew) {
  const items = [];
  
  EMERGENCY_EQUIPMENT_PROCEDURES_ITEMS.forEach(item => {
    // Check trigger condition
    let includeItem = false;
    
    if (item.trigger === 'Always') {
      includeItem = true;
    } else if (typeof item.trigger === 'function') {
      includeItem = item.trigger(vessel);
    }
    
    if (includeItem) {
      // Add rank visibility based on compliance_tags.md format
      const processedItem = {
        ...item,
        rankVisibility: {
          'Master': 'full_operational',
          'Engineer': 'full_operational',
          'Crew': 'full_operational', 
          'Crew Member': 'full_operational',
          'Supernumerary': 'general_awareness_supervised'
        }
      };
      
      items.push(processedItem);
    }
  });
  
  return {
    section: 'emergency_equipment_procedures',
    title: 'Emergency Equipment & Procedures',
    items: items,
    completionRequired: true,
    auditTrail: true
  };
}